import { useState, useEffect } from 'react';
import { CalendarDays, Sparkles, Dumbbell, ChevronDown, ChevronUp, Moon, Loader2 } from 'lucide-react';
import { aiApi } from '@/services/api';
import { Card, Button } from '@/components/common';
import { useUIStore } from '@/store/uiStore';
import type { AiTrainingPlan, WorkoutGoal, TrainingPlanType } from '@/types';

const GOAL_LABELS: Record<string, string> = {
  increase_weight: 'Súly növelése',
  increase_reps: 'Ismétlésszám növelése',
  increase_time: 'Idő növelése',
  general: 'Általános fejlődés',
};

const STATUS_LABELS: Record<string, string> = {
  generated: 'Generált',
  active: 'Aktív',
  completed: 'Befejezett',
  archived: 'Archivált',
};

const TrainingPlan = () => {
  const { showToast } = useUIStore();
  const [planName, setPlanName] = useState('');
  const [planType, setPlanType] = useState<TrainingPlanType>('fixed');
  const [durationValue, setDurationValue] = useState(4);
  const [durationUnit, setDurationUnit] = useState<'weeks' | 'days'>('weeks');
  const [goal, setGoal] = useState<WorkoutGoal>('general');

  const [generating, setGenerating] = useState(false);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [plans, setPlans] = useState<AiTrainingPlan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<AiTrainingPlan | null>(null);
  const [expandedDays, setExpandedDays] = useState<Set<number>>(new Set([1]));

  const toggleDay = (dayNum: number) => {
    setExpandedDays((prev) => {
      const next = new Set(prev);
      if (next.has(dayNum)) next.delete(dayNum);
      else next.add(dayNum);
      return next;
    });
  };

  const loadPlans = async () => {
    setLoadingPlans(true);
    try {
      const res = await aiApi.getTrainingPlans();
      if (res.success && res.data?.plans) {
        setPlans(res.data.plans);
      }
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'message' in err ? String((err as { message: string }).message) : 'Ismeretlen hiba';
      showToast(`Tervek betöltése sikertelen: ${msg}`, 'error');
    } finally {
      setLoadingPlans(false);
    }
  };

  useEffect(() => {
    loadPlans();
  }, []);

  const handleGenerate = async () => {
    if (!planName.trim()) {
      showToast('Add meg a terv nevét!', 'warning');
      return;
    }

    setGenerating(true);
    try {
      const payload: {
        name: string;
        goal: string;
        planType: string;
        durationDays?: number;
        durationWeeks?: number;
      } = {
        name: planName.trim(),
        goal,
        planType,
      };

      if (planType === 'fixed') {
        if (durationUnit === 'weeks') {
          payload.durationWeeks = durationValue;
        } else {
          payload.durationDays = durationValue;
        }
      }

      const res = await aiApi.generateTrainingPlan(payload);
      if (res.success && res.data?.plan) {
        setSelectedPlan(res.data.plan);
        setExpandedDays(new Set([1]));
        loadPlans();
        showToast('Edzésterv sikeresen generálva!', 'success');
      } else {
        const err = res.error || { message: 'Ismeretlen hiba' };
        showToast(err.message || 'Generálás sikertelen', 'error');
      }
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'message' in err ? String((err as { message: string }).message) : 'Ismeretlen hiba';
      showToast(`Generálás sikertelen: ${msg}`, 'error');
    } finally {
      setGenerating(false);
    }
  };

  const handleViewPlan = async (id: string) => {
    try {
      const res = await aiApi.getTrainingPlan(id);
      if (res.success && res.data?.plan) {
        setSelectedPlan(res.data.plan);
        setExpandedDays(new Set([1]));
      }
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'message' in err ? String((err as { message: string }).message) : 'Ismeretlen hiba';
      showToast(`Terv betöltése sikertelen: ${msg}`, 'error');
    }
  };

  const plan = selectedPlan?.generatedPlan;
  const usage = selectedPlan
    ? { totalTokens: selectedPlan.totalTokens, costUsd: selectedPlan.costUsd, model: selectedPlan.model }
    : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <CalendarDays className="w-7 h-7 text-primary-500" />
          Edzésterv összeállító
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">AI alapú hosszú távú edzésterv készítés</p>
      </div>

      {/* Settings Section */}
      <Card title="Beállítások">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Terv neve</label>
            <input
              type="text"
              placeholder="pl. 4 hetes erőnövelő program"
              value={planName}
              onChange={(e) => setPlanName(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Terv típusa</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPlanType('fixed')}
                className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                  planType === 'fixed'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                Fix időszak
              </button>
              <button
                type="button"
                onClick={() => setPlanType('continuous')}
                className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                  planType === 'continuous'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                Folyamatos
              </button>
            </div>
          </div>

          {planType === 'fixed' && (
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Időtartam</label>
                <input
                  type="number"
                  min={1}
                  max={52}
                  value={durationValue}
                  onChange={(e) => setDurationValue(Math.max(1, parseInt(e.target.value, 10) || 1))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setDurationUnit('weeks')}
                  className={`px-4 py-2 rounded-lg font-medium ${
                    durationUnit === 'weeks'
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                  }`}
                >
                  Hét
                </button>
                <button
                  type="button"
                  onClick={() => setDurationUnit('days')}
                  className={`px-4 py-2 rounded-lg font-medium ${
                    durationUnit === 'days'
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                  }`}
                >
                  Nap
                </button>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Cél</label>
            <div className="flex flex-wrap gap-2">
              {(Object.keys(GOAL_LABELS) as WorkoutGoal[]).map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => setGoal(g)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    goal === g
                      ? 'bg-primary-600 text-white dark:bg-primary-500'
                      : 'border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {GOAL_LABELS[g]}
                </button>
              ))}
            </div>
          </div>

          <Button
            onClick={handleGenerate}
            disabled={generating}
            loading={generating}
            icon={<Sparkles className="w-4 h-4" />}
            className="w-full sm:w-auto"
          >
            Edzésterv generálása
          </Button>
        </div>
      </Card>

      {/* Loading State */}
      {generating && (
        <Card>
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Loader2 className="w-12 h-12 text-primary-500 animate-spin mb-4" />
            <p className="text-lg font-medium text-gray-900 dark:text-gray-100">Edzésterv generálása folyamatban...</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Ez akár 1-2 percet is igénybe vehet...</p>
          </div>
        </Card>
      )}

      {/* Result Section */}
      {!generating && selectedPlan && plan && (
        <Card
          title="Generált edzésterv"
          headerActions={
            usage && (
              <div className="flex flex-wrap gap-2 text-sm">
                <span className="px-2 py-1 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200">
                  {usage.model || '-'}
                </span>
                <span className="px-2 py-1 rounded bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200">
                  {(usage.totalTokens || 0).toLocaleString('hu')} token
                </span>
                <span className="px-2 py-1 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200">
                  ${(usage.costUsd || 0).toFixed(6)}
                </span>
              </div>
            )
          }
        >
          <div className="space-y-6">
            {plan.summary && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Összefoglaló</h4>
                <p className="text-gray-600 dark:text-gray-400">{plan.summary}</p>
              </div>
            )}

            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Napok</h4>
              <div className="space-y-2">
                {(plan.days || []).map((day) => {
                  const isExpanded = expandedDays.has(day.dayNumber);
                  return (
                    <div
                      key={day.dayNumber}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-800"
                    >
                      <button
                        type="button"
                        onClick={() => toggleDay(day.dayNumber)}
                        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <span className="font-medium text-gray-900 dark:text-gray-100">
                            {day.dayNumber}. nap – {day.dayName}
                          </span>
                          <span className="text-sm text-gray-500 dark:text-gray-400">{day.focus}</span>
                          {day.isRestDay && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 text-xs font-medium">
                              <Moon className="w-3 h-3" />
                              Pihenőnap
                            </span>
                          )}
                        </div>
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-gray-500" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-500" />
                        )}
                      </button>
                      {isExpanded && (
                        <div className="px-4 pb-4 pt-0 border-t border-gray-200 dark:border-gray-700">
                          {day.isRestDay ? (
                            <p className="text-gray-600 dark:text-gray-400 text-sm py-2">{day.notes || 'Pihenőnap – regeneráció.'}</p>
                          ) : (
                            <div className="space-y-3 pt-2">
                              {(day.exercises || []).map((ex, i) => (
                                <div key={i} className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Dumbbell className="w-4 h-4 text-primary-500" />
                                    <span className="font-medium text-gray-900 dark:text-gray-100">{ex.name}</span>
                                    {ex.muscleGroup && (
                                      <span className="text-xs px-2 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                                        {ex.muscleGroup}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex flex-wrap gap-2">
                                    {(ex.sets || []).map((set, si) => (
                                      <span
                                        key={si}
                                        className="text-sm text-gray-600 dark:text-gray-400"
                                      >
                                        {set.reps != null && `${set.reps} ism.`}
                                        {set.weight != null && ` ${set.weight} kg`}
                                        {set.durationSeconds != null && ` ${set.durationSeconds} mp`}
                                        {set.restSeconds != null && ` (${set.restSeconds} mp pihenő)`}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {plan.weeklyNotes && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Heti megjegyzések</h4>
                <p className="text-gray-600 dark:text-gray-400">{plan.weeklyNotes}</p>
              </div>
            )}

            {plan.notes && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Általános megjegyzések</h4>
                <p className="text-gray-600 dark:text-gray-400">{plan.notes}</p>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Previous Plans */}
      <Card
        title="Korábbi tervek"
        headerActions={
          <Button variant="ghost" size="sm" onClick={loadPlans} disabled={loadingPlans}>
            Frissítés
          </Button>
        }
      >
        {loadingPlans ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
          </div>
        ) : plans.length === 0 ? (
          <p className="text-center py-8 text-gray-500 dark:text-gray-400">Még nincs generált terv.</p>
        ) : (
          <div className="space-y-2">
            {plans.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => handleViewPlan(p.id)}
                className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${
                  selectedPlan?.id === p.id
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{p.name}</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">
                      {p.durationWeeks ? `${p.durationWeeks} hét` : p.durationDays ? `${p.durationDays} nap` : '-'} • {GOAL_LABELS[p.goal] || p.goal}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="px-2 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                      {STATUS_LABELS[p.status] || p.status}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400">${(p.costUsd || 0).toFixed(6)}</span>
                    <span className="text-gray-400 dark:text-gray-500">
                      {new Date(p.createdAt).toLocaleDateString('hu-HU')}
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default TrainingPlan;
